<script setup lang="ts">
import type { ProfileCustomerAddress, ProfileAddressRow } from '~/composables/useAuth';
import BaseButton from '~/components/base/BaseButton.vue';
import BaseConfirmDialog from '~/components/base/BaseConfirmDialog.vue';
import AreaRiservataProfiloEditPanel from '~/components/area-riservata/AreaRiservataProfiloEditPanel.vue';
import AreaRiservataIndirizzoEditPanel from '~/components/area-riservata/AreaRiservataIndirizzoEditPanel.vue';
import AreaRiservataIndirizzoAddPanel from '~/components/area-riservata/AreaRiservataIndirizzoAddPanel.vue';

definePageMeta({
	layout: 'area-riservata',
	middleware: 'auth',
});

const { user, customer, logout, fetchUser } = useAuth();
const { authFetch } = useAuthFetch();
const router = useRouter();
const config = useRuntimeConfig();

const directusUrl = config.public.directusUrl as string;
const avatarUrl = computed(() => {
	const id = user.value?.avatar;
	if (!id || !directusUrl) return null;
	return `${directusUrl}/assets/${id}`;
});

const displayName = computed(() => {
	const u = user.value;
	const c = customer.value;
	const first = c?.first_name ?? u?.first_name;
	const last = c?.last_name ?? u?.last_name;
	if (first || last) return `${first || ''} ${last || ''}`.trim();
	return u?.email || 'Utente';
});

const email = computed(() => user.value?.email ?? customer.value?.email ?? '—');
const phone = computed(() => customer.value?.phone ?? '—');
const genderLabel = computed(() => {
	const g = customer.value?.gender;
	if (!g) return '—';
	const map: Record<string, string> = {
		male: 'Uomo',
		female: 'Donna',
		transgender: 'Transgender',
		agender: 'Non binario',
		'not-specified': 'Non specificato',
	};
	return map[g] ?? g;
});
const dateOfBirthFormatted = computed(() => {
	const d = customer.value?.date_of_birth;
	if (!d) return '—';
	try {
		const date = new Date(d);
		return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
	} catch {
		return d;
	}
});

function formatAddressLine(addr: ProfileAddressRow | null | undefined): string {
	if (!addr) return '—';
	const parts: string[] = [];
	if (addr.street) parts.push(addr.street);
	const city = typeof addr.city === 'object' && addr.city?.name ? addr.city.name : null;
	const zip = typeof addr.zipcode === 'object' && addr.zipcode?.locality ? addr.zipcode.locality : null;
	if (zip && city) parts.push(`${zip} ${city}`);
	else if (city) parts.push(city);
	else if (zip) parts.push(zip);
	const province = typeof addr.province === 'object' && addr.province?.name ? addr.province.name : null;
	if (province) parts.push(province);
	const country = typeof addr.country === 'object' && addr.country?.name ? addr.country.name : null;
	if (country) parts.push(country);
	return parts.length ? parts.join(', ') : '—';
}

const addresses = computed<ProfileCustomerAddress[]>(() => {
	const list = customer.value?.addresses;
	return Array.isArray(list) ? list : [];
});

const showEditPanel = ref(false);
const showAddressPanel = ref(false);
const showAddAddressPanel = ref(false);
const editingAddress = ref<ProfileCustomerAddress | null>(null);
const isDeletingAccount = ref(false);
const deleteAccountError = ref<string | null>(null);

interface StaffResumeFile {
	id: string;
	filename_download?: string | null;
}

interface StaffForCustomer {
	id: number;
	name: string | null;
	surname: string | null;
	job_position: string | null;
	public_avatar: string | null;
	description: string | null;
	resume_files: StaffResumeFile[];
}

const customerStaff = ref<StaffForCustomer[]>([]);
const selectedStaff = ref<StaffForCustomer | null>(null);
const isStaffLoading = ref(false);
const staffError = ref<string | null>(null);
const visibleStaffIds = ref<number[]>([]);
const staffSliderRef = ref<HTMLElement | null>(null);
const staffSectionRef = ref<HTMLElement | null>(null);
const ordersSectionRef = ref<HTMLElement | null>(null);
const currentStaffStartIndex = ref(0);
const staffSliderReady = ref(false);
const isStaffAtStart = ref(true);
const isStaffAtEnd = ref(false);

const staffDots = computed(() =>
	customerStaff.value.map((staff, index) => ({ staff, index })).slice(0, 6),
);

interface ProfilePurchasedService {
	id: number;
	service_name: string | null;
	service_color: string | null;
	purchased_quantity: number;
	used_quantity: number;
	remaining_quantity: number;
	order_id: number | null;
	order_invoice_code: string | null;
	order_status: string | null;
	purchase_date: string | null;
	valid_to: string | null;
}

const purchasedServices = ref<ProfilePurchasedService[]>([]);
const isPurchasedServicesLoading = ref(false);
const purchasedServicesError = ref<string | null>(null);
const visiblePurchasedServiceIds = ref<number[]>([]);

interface OrderServiceSummary {
	id: number;
	purchased_quantity: number;
	used_quantity: number;
	remaining_quantity: number | null;
	service_name: string | null;
	service_color: string | null;
}

interface OrderPaymentSummary {
	due_date: string | null;
	status: string | null;
}

interface OrderSummary {
	id: number;
	invoice_code: string | null;
	status: string | null;
	purchase_date: string | null;
	total_gross_price: number | null;
	total_net_price: number | null;
	total_payed: number | null;
	total_to_be_payed: number | null;
	total_expired: number | null;
	customer_purchased_services: OrderServiceSummary[];
	payments: OrderPaymentSummary[];
}

const allOrders = ref<OrderSummary[]>([]);
const ordersPerPage = 3;
const currentOrdersPage = ref(1);
const paginatedOrders = computed(() => {
	if (!allOrders.value.length) return [];
	const start = (currentOrdersPage.value - 1) * ordersPerPage;
	return allOrders.value.slice(start, start + ordersPerPage);
});
const totalOrdersPages = computed(() =>
	allOrders.value.length ? Math.ceil(allOrders.value.length / ordersPerPage) : 1,
);
const isAllOrdersLoading = ref(false);
const allOrdersError = ref<string | null>(null);
const selectedOrder = ref<any | null>(null);
const isOrderDetailLoading = ref(false);
const orderDetailError = ref<string | null>(null);
const isStaffSectionVisible = ref(false);
const hasStartedStaffAnimation = ref(false);
const isOrdersSectionVisible = ref(false);
const hasStartedOrdersAnimation = ref(false);

interface WizardSubmissionAnswerForClient {
	id: number;
	question_id: number | null;
	question: string | null;
	answer: string | null;
	answer_json: string | null;
}

interface WizardSubmissionForClient {
	id: number;
	status: string | null;
	wizard_id: number | null;
	wizard_title: string | null;
	score_result_id: number | null;
	score_result_title: string | null;
	score_result_icon: string | null;
	score_result_color: string | null;
	score_result_description: string | null;
	date_created: string | null;
	points_score_json: string | null;
	ai_score_evaluation: string | null;
	answers: WizardSubmissionAnswerForClient[];
}

interface LatestWizardInfo {
	id: number;
	title: string | null;
	has_completed_submission: boolean;
}

const wizardSubmissions = ref<WizardSubmissionForClient[]>([]);
const isWizardSubmissionsLoading = ref(false);
const wizardSubmissionsError = ref<string | null>(null);
const latestWizard = ref<LatestWizardInfo | null>(null);
const selectedSubmission = ref<WizardSubmissionForClient | null>(null);
const isDeletingSubmission = ref(false);
const showDeleteSubmissionDialog = ref(false);
const showDeleteAccountDialog = ref(false);

function staffFullName(s: StaffForCustomer): string {
	const parts = [s.name, s.surname].filter(Boolean);
	return parts.length ? parts.join(' ') : 'Staff WeFocus';
}

function staffAvatarUrl(s: StaffForCustomer): string | null {
	if (!s.public_avatar || !directusUrl) return null;
	return `${directusUrl}/assets/${s.public_avatar}`;
}

function staffResumeUrl(file: StaffResumeFile): string | null {
	if (!file.id || !directusUrl) return null;
	return `${directusUrl}/assets/${file.id}?download`;
}

async function loadCustomerStaff() {
	isStaffLoading.value = true;
	staffError.value = null;
	visibleStaffIds.value = [];
	currentStaffStartIndex.value = 0;
	staffSliderReady.value = false;
	isStaffAtStart.value = true;
	isStaffAtEnd.value = false;
	try {
		const res = await authFetch<{ staff: StaffForCustomer[] }>('/api/area-riservata/customer-staff');
		customerStaff.value = Array.isArray(res.staff) ? res.staff : [];
	} catch (error: any) {
		const message =
			error?.data?.statusMessage ||
			error?.data?.message ||
			error?.message ||
			'Errore nel caricamento dello staff.';
		staffError.value = message;
		customerStaff.value = [];
	} finally {
		isStaffLoading.value = false;
		if (customerStaff.value.length) {
			maybeStartStaffAnimation();
		}
	}
}

function updateStaffEdges() {
	const el = staffSliderRef.value;
	if (!el) return;
	const scrollLeft = el.scrollLeft;
	const maxScroll = el.scrollWidth - el.clientWidth;
	isStaffAtStart.value = scrollLeft <= 2;
	isStaffAtEnd.value = scrollLeft >= maxScroll - 2;
}

function onStaffScroll() {
	updateStaffEdges();
}

function scrollToStaffIndex(index: number) {
	const container = staffSliderRef.value;
	if (!container) return;
	const cards = container.querySelectorAll<HTMLButtonElement>('[data-staff-card="true"]');
	if (!cards.length) return;
	const clampedIndex = Math.max(0, Math.min(index, cards.length - 1));
	const target = cards[clampedIndex];
	if (!target) return;
	container.scrollTo({
		left: target.offsetLeft,
		behavior: 'smooth',
	});
	currentStaffStartIndex.value = clampedIndex;
	updateStaffEdges();
}

function scrollStaff(direction: 'prev' | 'next') {
	const total = customerStaff.value.length;
	if (!total) return;
	const current = currentStaffStartIndex.value;
	const nextIndex = direction === 'next' ? Math.min(current + 1, total - 1) : Math.max(current - 1, 0);
	if (nextIndex !== current) {
		scrollToStaffIndex(nextIndex);
	}
}

async function maybeStartStaffAnimation() {
	if (hasStartedStaffAnimation.value || !isStaffSectionVisible.value || !customerStaff.value.length) return;
	hasStartedStaffAnimation.value = true;
	visibleStaffIds.value = [];
	await nextTick();
	const total = customerStaff.value.length;
	customerStaff.value.forEach((staff, index) => {
		setTimeout(() => {
			visibleStaffIds.value.push(staff.id);
			if (index === total - 1) {
				staffSliderReady.value = true;
				updateStaffEdges();
			}
		}, index * 280);
	});
}

function calculateServiceUsagePercentage(service: {
	purchased_quantity: number;
	remaining_quantity: number | null;
}): number {
	const total = service.purchased_quantity || 0;
	if (!total) return 0;
	const remaining = service.remaining_quantity ?? 0;
	const used = Math.max(total - remaining, 0);
	const ratio = used / total;
	if (!Number.isFinite(ratio) || ratio < 0) return 0;
	if (ratio > 1) return 1;
	return ratio;
}

function formatOrderLabel(service: ProfilePurchasedService): string | null {
	if (service.valid_to) {
		try {
			const d = new Date(service.valid_to);
			const date = d.toLocaleDateString('it-IT');
			return `Scadenza: ${date}`;
		} catch {
			return `Scadenza: ${service.valid_to}`;
		}
	}

	if (service.purchase_date) {
		try {
			const d = new Date(service.purchase_date);
			const date = d.toLocaleDateString('it-IT');
			return `Acquistato: ${date}`;
		} catch {
			return `Acquistato: ${service.purchase_date}`;
		}
	}

	return null;
}

async function loadCustomerPurchasedServices() {
	isPurchasedServicesLoading.value = true;
	purchasedServicesError.value = null;
	purchasedServices.value = [];
	visiblePurchasedServiceIds.value = [];
	try {
		const res = await authFetch<{ services: ProfilePurchasedService[] }>(
			'/api/area-riservata/customer-purchased-services',
		);
		const list = Array.isArray(res.services) ? res.services : [];
		purchasedServices.value = list.filter((s) => {
			const total = s.purchased_quantity || 0;
			const remaining = s.remaining_quantity || 0;
			return total > 0 && remaining > 0 && remaining < total;
		});
		if (purchasedServices.value.length) {
			await nextTick();
			maybeStartOrdersAnimation();
		}
	} catch (error: any) {
		const message =
			error?.data?.statusMessage ||
			error?.data?.message ||
			error?.message ||
			'Errore nel caricamento degli ordini.';
		purchasedServicesError.value = message;
		purchasedServices.value = [];
	} finally {
		isPurchasedServicesLoading.value = false;
	}
}

function getServiceColor(service: ProfilePurchasedService): string {
	return service.service_color || '#A21B1F';
}

function getOrderServiceColors(order: OrderSummary): string[] {
	const colors = (order.customer_purchased_services || [])
		.map((s) => s.service_color)
		.filter((c): c is string => !!c);

	if (!colors.length) {
		return ['#A21B1F'];
	}

	// Rimuove duplicati mantenendo l'ordine
	return Array.from(new Set(colors));
}

function getOrderPurchasedServices(order: OrderSummary): OrderServiceSummary[] {
	return order.customer_purchased_services || [];
}

function getOrderNextPaymentDueDate(order: { payments?: { due_date: string | null; status: string | null }[] }): string | null {
	if (!order.payments || !order.payments.length) return null;

	const upcoming = order.payments
		.filter((p) => p.due_date && p.status !== 'payed')
		.slice()
		.sort((a, b) => {
			const da = new Date(a.due_date as string).getTime();
			const db = new Date(b.due_date as string).getTime();
			return da - db;
		});

	const next = upcoming[0];
	return next?.due_date ?? null;
}

async function maybeStartOrdersAnimation() {
	if (hasStartedOrdersAnimation.value || !isOrdersSectionVisible.value || !purchasedServices.value.length) return;
	hasStartedOrdersAnimation.value = true;
	visiblePurchasedServiceIds.value = [];
	await nextTick();
	const total = purchasedServices.value.length;
	purchasedServices.value.forEach((service, index) => {
		setTimeout(() => {
			visiblePurchasedServiceIds.value.push(service.id);
		}, index * 300);
	});
}

async function loadAllOrders() {
	if (isAllOrdersLoading.value || allOrders.value.length) return;
	isAllOrdersLoading.value = true;
	allOrdersError.value = null;
	try {
		const res = await authFetch<{
			orders: OrderSummary[];
		}>('/api/area-riservata/orders');
		allOrders.value = Array.isArray(res.orders) ? res.orders : [];
		currentOrdersPage.value = 1;
	} catch (error: any) {
		const message =
			error?.data?.statusMessage ||
			error?.data?.message ||
			error?.message ||
			'Errore nel caricamento degli ordini.';
		allOrdersError.value = message;
		allOrders.value = [];
	} finally {
		isAllOrdersLoading.value = false;
	}
}

function formatOrderStatus(status: string | null): string {
	if (!status) return '—';
	const map: Record<string, string> = {
		draft: 'Bozza',
		'to-be-payed': 'Da pagare',
		payed: 'Pagato',
		expired: 'Scaduto',
	};
	return map[status] ?? status;
}

function getOrderStatusChipClass(status: string | null): string {
	switch (status) {
		case 'to-be-payed':
			// Blu per "Da pagare"
			return 'border-blue-200 bg-blue-50 text-blue-700';
		case 'payed':
			// Verde per "Pagato"
			return 'border-emerald-200 bg-emerald-50 text-emerald-700';
		case 'expired':
			// Rosso per "Scaduto"
			return 'border-red-200 bg-red-50 text-red-700';
		default:
			return 'border-gray-200 bg-gray-50 text-gray-700';
	}
}

function formatOrderDate(date: string | null): string {
	if (!date) return '—';
	try {
		const d = new Date(date);
		return d.toLocaleDateString('it-IT');
	} catch {
		return date;
	}
}

function formatCurrency(amount: number | null): string {
	if (amount == null) return '—';
	try {
		return new Intl.NumberFormat('it-IT', {
			style: 'currency',
			currency: 'EUR',
			maximumFractionDigits: 2,
		}).format(amount);
	} catch {
		return `${amount} €`;
	}
}

function formatWizardSubmissionDate(date: string | null): string {
	return formatOrderDate(date);
}

function parseWizardAnswerJson(raw: string | null | undefined): string | null {
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw);
		if (Array.isArray(parsed)) {
			// Array di primitive
			if (!parsed.length) return null;
			if (typeof parsed[0] !== 'object') {
				return parsed.map((v) => String(v)).join('; ');
			}

			// Array di oggetti { value, text, other_text? }
			const texts = parsed
				.map((item: any) => {
					if (!item || typeof item !== 'object') return null;
					if (item.other_text) return `Altro: ${String(item.other_text)}`;
					if (item.text) return String(item.text);
					if (item.label) return String(item.label);
					// fallback: evita il campo "value", usa gli altri
					const values = Object.entries(item)
						.filter(([key, val]) => key !== 'value' && val != null && val !== '')
						.map(([, val]) => String(val));
					return values.length ? values.join(' ') : null;
				})
				.filter((v): v is string => !!v);

			return texts.length ? texts.join('; ') : null;
		}
		if (parsed && typeof parsed === 'object') {
			const obj = parsed as any;

			// Oggetto singolo { value, text, other_text? }
			if (obj.other_text) return `Altro: ${String(obj.other_text)}`;
			if (obj.text) return String(obj.text);
			if (obj.label) return String(obj.label);

			// fallback: evita il campo "value", usa gli altri
			const values = Object.entries(obj)
				.filter(([key, val]) => key !== 'value' && val != null && val !== '')
				.map(([, val]) => String(val));
			return values.length ? values.join(' ') : null;
		}
		return String(parsed);
	} catch {
		return raw;
	}
}

function formatWizardAnswer(answer: WizardSubmissionAnswerForClient): string {
	const fromJson = parseWizardAnswerJson(answer.answer_json);
	if (fromJson) return fromJson;
	return answer.answer || '—';
}

async function loadWizardSubmissions() {
	isWizardSubmissionsLoading.value = true;
	wizardSubmissionsError.value = null;
	wizardSubmissions.value = [];
	latestWizard.value = null;
	try {
		const res = await authFetch<{
			submissions: WizardSubmissionForClient[];
			latest_wizard: LatestWizardInfo | null;
		}>('/api/area-riservata/wizard-submissions');

		wizardSubmissions.value = Array.isArray(res.submissions) ? res.submissions : [];
		latestWizard.value = res.latest_wizard ?? null;
	} catch (error: any) {
		const message =
			error?.data?.statusMessage ||
			error?.data?.message ||
			error?.message ||
			'Errore nel caricamento dei questionari.';
		wizardSubmissionsError.value = message;
		wizardSubmissions.value = [];
		latestWizard.value = null;
	} finally {
		isWizardSubmissionsLoading.value = false;
	}
}

async function deleteWizardSubmission(submission: WizardSubmissionForClient) {
	if (!submission?.id || isDeletingSubmission.value) return;

	isDeletingSubmission.value = true;
	try {
		await authFetch(`/api/area-riservata/wizard-submissions/${submission.id}`, {
			method: 'DELETE',
		});

		// Aggiorna la lista locale
		wizardSubmissions.value = wizardSubmissions.value.filter((s) => s.id !== submission.id);
		selectedSubmission.value = null;

		// Ricarica latestWizard per aggiornare eventuale CTA
		await loadWizardSubmissions();
	} catch (error: any) {
		const message =
			error?.data?.statusMessage ||
			error?.data?.message ||
			error?.message ||
			'Errore nell\'eliminazione del questionario.';
		wizardSubmissionsError.value = message;
	} finally {
		isDeletingSubmission.value = false;
	}
}

function confirmDeleteSelectedSubmission() {
	if (!selectedSubmission.value || isDeletingSubmission.value) return;
	deleteWizardSubmission(selectedSubmission.value);
	showDeleteSubmissionDialog.value = false;
	selectedSubmission.value = null;
}

async function openOrderDetail(orderId: number) {
	selectedOrder.value = null;
	orderDetailError.value = null;
	isOrderDetailLoading.value = true;
	try {
		const res = await authFetch<{ order: any }>(`/api/area-riservata/orders/${orderId}`);
		selectedOrder.value = res.order;
	} catch (error: any) {
		const message =
			error?.data?.statusMessage ||
			error?.data?.message ||
			error?.message ||
			'Errore nel caricamento del dettaglio ordine.';
		orderDetailError.value = message;
	} finally {
		isOrderDetailLoading.value = false;
	}
}

function openEditProfile() {
	showEditPanel.value = true;
}
function closeEditProfile() {
	showEditPanel.value = false;
}
function openEditAddress(addr: ProfileCustomerAddress) {
	editingAddress.value = addr;
	showAddressPanel.value = true;
}
function closeEditAddress() {
	editingAddress.value = null;
	showAddressPanel.value = false;
}
function openAddAddress() {
	showAddAddressPanel.value = true;
}
function closeAddAddress() {
	showAddAddressPanel.value = false;
}

function goToResetPassword() {
	const emailQuery = user.value?.email ? `?email=${encodeURIComponent(user.value.email)}` : '';
	router.push(`/forgot-password${emailQuery}`);
}

const handleLogout = async () => {
	const result = await logout();
	if (result.success) {
		await router.push('/login');
	}
};

const handleDeleteAccount = async () => {
	if (!user.value?.id || isDeletingAccount.value) return;

	isDeletingAccount.value = true;
	deleteAccountError.value = null;
	try {
		await authFetch(`${directusUrl}/users/${user.value.id}`, {
			method: 'DELETE',
		});

		await handleLogout();
	} catch (error: any) {
		const message =
			error?.data?.statusMessage ||
			error?.data?.message ||
			error?.message ||
			"Errore nell'eliminazione dell'account. Riprova più tardi.";
		deleteAccountError.value = message;
	} finally {
		isDeletingAccount.value = false;
	}
};

function confirmDeleteAccount() {
	if (isDeletingAccount.value) return;
	showDeleteAccountDialog.value = false;
	handleDeleteAccount();
}

function onProfileSaved() {
	closeEditProfile();
	fetchUser();
}
function onAddressSaved() {
	closeEditAddress();
	fetchUser();
}
function onAddressAdded() {
	closeAddAddress();
	fetchUser();
}

let sectionsObserver: IntersectionObserver | null = null;

function setupSectionsObserver() {
	if (!process.client) return;
	if (sectionsObserver) return;
	if (!staffSectionRef.value && !ordersSectionRef.value) return;

	sectionsObserver = new IntersectionObserver(
		(entries) => {
			for (const entry of entries) {
				if (!entry.isIntersecting) continue;
				if (entry.target === staffSectionRef.value) {
					isStaffSectionVisible.value = true;
					maybeStartStaffAnimation();
				}
				if (entry.target === ordersSectionRef.value) {
					isOrdersSectionVisible.value = true;
					maybeStartOrdersAnimation();
				}
			}
		},
		{
			threshold: 0.2,
		},
	);

	if (staffSectionRef.value) {
		sectionsObserver.observe(staffSectionRef.value);
	}
	if (ordersSectionRef.value) {
		sectionsObserver.observe(ordersSectionRef.value);
	}
}

onMounted(async () => {
	if (!user.value) {
		await fetchUser();
	}
	await loadCustomerStaff();
	await loadCustomerPurchasedServices();
	await loadWizardSubmissions();
	await loadAllOrders();
	setupSectionsObserver();
});

onUnmounted(() => {
	if (sectionsObserver) {
		sectionsObserver.disconnect();
		sectionsObserver = null;
	}
});
</script>

<template>
	<div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
		<h1 class="text-2xl font-bold text-gray-900 mb-6">Il tuo profilo</h1>

		<!-- Card profilo: renderizzata solo lato client per evitare hydration mismatch (user/customer da useAuth sono disponibili dopo fetch in onMounted) -->
		<ClientOnly>
			<div class="border border-gray-200 bg-white shadow-sm overflow-hidden transition-shadow">
				<div class="p-4 sm:p-6">
					<div class="min-w-0 flex-1 space-y-6">
						<div class="grid grid-cols-1 md:grid-cols-4 gap-x-6 gap-y-4 text-sm items-start">
							<!-- Avatar -->
							<div class="flex justify-center md:justify-start">
								<div class="w-24 h-24 rounded-full overflow-hidden bg-gray-100 ring-2 ring-gray-200">
									<img
										v-if="avatarUrl"
										:src="avatarUrl"
										:alt="displayName"
										class="w-full h-full object-cover"
									>
									<span
										v-else
										class="w-full h-full flex items-center justify-center text-gray-400"
										aria-hidden="true"
									>
										<span class="material-symbols-outlined text-4xl">person</span>
									</span>
								</div>
							</div>

							<!-- Dati profilo -->
							<dl class="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3 text-sm">
								<div>
									<dt class="font-medium text-gray-500">Nome</dt>
									<dd class="mt-0.5 text-gray-900">
										{{ customer?.first_name ?? user?.first_name ?? '—' }}
									</dd>
								</div>
								<div>
									<dt class="font-medium text-gray-500">Cognome</dt>
									<dd class="mt-0.5 text-gray-900">
										{{ customer?.last_name ?? user?.last_name ?? '—' }}
									</dd>
								</div>
								<div>
									<dt class="font-medium text-gray-500">Email</dt>
									<dd class="mt-0.5 text-gray-900">
										{{ email }}
									</dd>
								</div>
								<div>
									<dt class="font-medium text-gray-500">Genere</dt>
									<dd class="mt-0.5 text-gray-900">
										{{ genderLabel }}
									</dd>
								</div>
								<div>
									<dt class="font-medium text-gray-500">Data di nascita</dt>
									<dd class="mt-0.5 text-gray-900">
										{{ dateOfBirthFormatted }}
									</dd>
								</div>
								<div>
									<dt class="font-medium text-gray-500">Telefono</dt>
									<dd class="mt-0.5 text-gray-900">
										{{ phone }}
									</dd>
								</div>
							</dl>
						</div>

						<!-- Indirizzi (full width) -->
						<div class="pt-2 border-t border-gray-100">
							<dt class="font-medium text-gray-500 mb-1">Indirizzi</dt>
							<dd class="mt-0.5">
								<ul
									v-if="addresses.length"
									class="grid grid-cols-1 gap-3 lg:grid-cols-2"
								>
									<li
										v-for="ca in addresses"
										:key="ca.id"
										class="flex flex-col gap-1 rounded-none border border-gray-100 bg-gray-50/50 p-3"
									>
										<div class="flex flex-wrap items-center gap-2">
											<span v-if="ca.label" class="text-sm font-medium text-gray-900">
												{{ ca.label }}
											</span>
											<span
												v-if="ca.is_default_billing"
												class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-800"
											>
												Fatturazione
											</span>
											<span
												v-if="ca.is_default_shipping"
												class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-800"
											>
												Spedizione
											</span>
										</div>
										<p class="text-sm text-gray-700">
											{{ formatAddressLine(ca.address) }}
										</p>
										<div class="flex gap-2 mt-1">
											<button
												type="button"
												class="text-sm font-medium font-button text-black hover:underline uppercase"
												aria-label="Modifica indirizzo"
												@click="openEditAddress(ca)"
											>
												Modifica
											</button>
										</div>
									</li>
								</ul>
								<p v-else class="text-gray-500">
									Nessun indirizzo.
								</p>
								<button
									type="button"
									class="mt-3 inline-flex items-center gap-2 text-sm font-medium font-button text-black hover:underline uppercase"
									@click="openAddAddress"
								>
									<span class="material-symbols-outlined text-lg">add</span>
									Aggiungi indirizzo
								</button>
							</dd>
						</div>

						<!-- CTA (full width) -->
						<div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2">
							<div class="flex flex-wrap gap-3">
								<button
									type="button"
									class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 hover:text-[#A21B1F] rounded-none transition-colors uppercase"
									@click="goToResetPassword"
								>
									<span class="material-symbols-outlined text-lg">lock_reset</span>
									Reset password
								</button>
								<button
									type="button"
									class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-black hover:bg-black/90 rounded-none transition-colors uppercase"
									@click="openEditProfile"
								>
									<span class="material-symbols-outlined text-lg">edit</span>
									Modifica
								</button>
							</div>
							<div class="flex justify-start sm:justify-end">
								<button
									type="button"
									class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-[#A21B1F] hover:bg-[#A21B1F]/90 rounded-none transition-colors uppercase"
									@click="handleLogout"
								>
									<span class="material-symbols-outlined text-lg">logout</span>
									Logout
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Blocco: IL TUO STAFF WEFOCUS -->
			<section
				ref="staffSectionRef"
				class="mt-10 md:mt-12"
			>
				<h2 class="text-lg font-bold text-gray-900 mb-6">IL TUO STAFF WEFOCUS</h2>

				<div v-if="isStaffLoading" class="-mx-4 sm:mx-0">
							<div class="px-4 sm:px-0">
						<div class="flex gap-4 overflow-x-auto pb-3 no-scrollbar">
							<div
								v-for="n in 3"
								:key="n"
								class="relative flex-shrink-0 min-w-[180px] bg-white border border-gray-200 rounded-none px-5 py-4"
							>
								<div class="flex flex-col items-center text-center gap-3 animate-pulse">
									<div class="w-14 h-14 rounded-full bg-gray-200" />
									<div class="w-24 h-3 rounded bg-gray-200" />
									<div class="w-32 h-3 rounded bg-gray-100" />
								</div>
							</div>
						</div>
					</div>
				</div>
				<div v-else-if="staffError" class="text-sm text-red-600">
					{{ staffError }}
				</div>
				<div v-else-if="!customerStaff.length" class="text-sm text-gray-500">
					Non è ancora stato associato alcun membro dello staff al tuo profilo.
				</div>
				<div v-else class="-mx-4 sm:mx-0">
					<div class="px-4 sm:px-0">
						<div class="relative flex items-center">
							<button
								v-if="staffSliderReady && customerStaff.length > 3 && !isStaffAtStart"
								type="button"
								class="hidden md:flex absolute -left-2 top-1/2 -mt-[14px] z-20 items-center justify-center h-7 w-7 rounded-full bg-white border border-gray-300 shadow-sm text-gray-600 hover:bg-gray-50"
								aria-label="Scorri staff indietro"
								@click="scrollStaff('prev')"
							>
								<span class="material-symbols-outlined text-base">chevron_left</span>
							</button>
							<button
								v-if="staffSliderReady && customerStaff.length > 3 && !isStaffAtEnd"
								type="button"
								class="hidden md:flex absolute -right-2 top-1/2 -mt-[14px] z-20 items-center justify-center h-7 w-7 rounded-full bg-white border border-gray-300 shadow-sm text-gray-600 hover:bg-gray-50"
								aria-label="Scorri staff avanti"
								@click="scrollStaff('next')"
							>
								<span class="material-symbols-outlined text-base">chevron_right</span>
							</button>

							<!-- Gradienti laterali per effetto "scomparsa" -->
							<div
								v-if="staffSliderReady && customerStaff.length > 3 && !isStaffAtStart"
								class="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-gray-50 to-transparent z-10"
								aria-hidden="true"
							/>
							<div
								v-if="staffSliderReady && customerStaff.length > 3 && !isStaffAtEnd"
								class="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-gray-50 to-transparent z-10"
								aria-hidden="true"
							/>

							<div
								ref="staffSliderRef"
								class="flex gap-4 overflow-x-auto pb-3 no-scrollbar"
								@scroll="onStaffScroll"
							>
								<button
									v-for="(staff, index) in customerStaff"
									:key="staff.id"
									type="button"
									data-staff-card="true"
									class="relative flex-shrink-0 min-w-[180px] bg-white border border-gray-200 shadow-sm hover:shadow-md rounded-none px-5 py-4 transform transition-transform transition-opacity duration-[900ms] ease-out"
									:class="visibleStaffIds.includes(staff.id) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'"
									@click="selectedStaff = staff"
								>
									<div class="flex flex-col items-center text-center gap-3">
										<div class="w-14 h-14 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
											<img
												v-if="staffAvatarUrl(staff)"
												:src="staffAvatarUrl(staff)!"
												:alt="staffFullName(staff)"
												class="w-full h-full object-cover"
											>
											<span
												v-else
												class="material-symbols-outlined text-3xl text-gray-400"
												aria-hidden="true"
											>
												person
											</span>
										</div>
										<div class="space-y-1">
											<p class="text-sm font-semibold text-gray-900 truncate">
												{{ staffFullName(staff) }}
											</p>
											<p class="text-xs text-gray-600 line-clamp-2">
												{{ staff.job_position || 'Professionista WeFocus' }}
											</p>
										</div>
									</div>
								</button>
							</div>
						</div>

						<div
							v-if="customerStaff.length > 3"
							class="flex justify-center gap-1 mt-2"
							aria-hidden="true"
						>
							<span
								v-for="dot in staffDots"
								:key="dot.staff.id"
								class="h-1.5 w-1.5 rounded-full transition-colors"
								:class="dot.index === currentStaffStartIndex ? 'bg-gray-900' : 'bg-gray-300'"
							/>
						</div>
					</div>
				</div>
			</section>

			<!-- Blocco: I TUOI ORDINI -->
			<section
				ref="ordersSectionRef"
				class="mt-10 md:mt-12"
			>
				<div class="mb-6">
					<h2 class="text-lg font-bold text-gray-900">
						I TUOI ORDINI
					</h2>
				</div>

				<!-- Vista riassunto ordini attivi (donut) -->
				<div class="-mx-4 sm:mx-0">
					<div
						v-if="isPurchasedServicesLoading"
						class="mx-0"
					>
						<div class="px-4 sm:px-0">
							<div class="bg-white border border-gray-200 shadow-sm rounded-none px-5 py-6">
								<div class="flex items-center justify-between mb-4">
									<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
										SERVIZI DA UTILIZZARE
									</h3>
								</div>
								<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
									<div
										v-for="n in 3"
										:key="n"
										class="flex items-center gap-4 animate-pulse"
									>
										<div class="w-16 h-16 rounded-full bg-gray-100" />
										<div class="flex-1 space-y-2">
											<div class="h-3 w-2/3 rounded bg-gray-200" />
											<div class="h-3 w-1/3 rounded bg-gray-100" />
											<div class="h-3 w-1/2 rounded bg-gray-100" />
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div
						v-else-if="purchasedServicesError"
						class="mx-0"
					>
						<div class="px-4 sm:px-0">
							<div class="bg-white border border-red-200 text-red-700 shadow-sm rounded-none px-5 py-5 text-sm">
								{{ purchasedServicesError }}
							</div>
						</div>
					</div>
					<div
						v-else-if="!purchasedServices.length"
						class="mx-0"
					>
						<div class="px-4 sm:px-0">
							<div class="bg-white border border-gray-200 shadow-sm rounded-none px-5 py-5 text-sm text-gray-500">
								<div class="flex items-center justify-between mb-2">
									<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
										SERVIZI DA UTILIZZARE
									</h3>
								</div>
								Non hai ancora servizi acquistati da utilizzare.
							</div>
						</div>
					</div>
					<div v-else class="mx-0">
						<div class="px-4 sm:px-0">
							<div class="bg-white border border-gray-200 shadow-sm rounded-none px-5 py-6">
								<div class="flex items-center justify-between mb-4">
									<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
										SERVIZI DA UTILIZZARE
									</h3>
								</div>
								<div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8">
									<article
										v-for="service in purchasedServices"
										:key="service.id"
										class="flex gap-5 items-center transform transition duration-700 ease-out"
										:class="visiblePurchasedServiceIds.includes(service.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'"
									>
										<div class="relative w-16 h-16">
											<svg
												viewBox="0 0 36 36"
												class="w-16 h-16 transform -rotate-90"
												aria-hidden="true"
											>
												<circle
													class="text-gray-200"
													stroke="currentColor"
													stroke-width="4"
													fill="transparent"
													r="16"
													cx="18"
													cy="18"
												/>
												<circle
													stroke-width="4"
													fill="transparent"
													r="16"
													cx="18"
													cy="18"
													:stroke="getServiceColor(service)"
													:stroke-dasharray="`${Math.round(calculateServiceUsagePercentage(service) * 100)}, 100`"
													stroke-linecap="round"
												/>
											</svg>
											<div class="absolute inset-0 flex flex-col items-center justify-center text-[11px] leading-tight text-gray-900">
												<span class="font-semibold">
													{{ Math.max(service.purchased_quantity - service.remaining_quantity, 0) }} / {{ service.purchased_quantity || 0 }}
												</span>
												<span class="text-[10px] text-gray-500">usati</span>
											</div>
										</div>
										<div class="min-w-0 flex-1">
											<p class="text-sm font-semibold text-gray-900 truncate">
												{{ service.service_name || 'Servizio' }}
											</p>
											<p class="text-xs text-gray-600 mt-1">
												Rimanenti: {{ service.remaining_quantity || 0 }}
											</p>
											<p
												v-if="formatOrderLabel(service)"
												class="text-xs text-gray-500 mt-1"
											>
												{{ formatOrderLabel(service) }}
											</p>
										</div>
									</article>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Elenco completo ordini sotto alla card servizi -->
				<div class="mt-6 -mx-4 sm:mx-0">
					<div class="px-4 sm:px-0">
						<div class="bg-white border border-gray-200 shadow-sm rounded-none px-5 py-5 space-y-4">
							<div class="flex items-center justify-between">
								<h3 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
									STORICO ORDINI
								</h3>
							</div>

							<div v-if="isAllOrdersLoading" class="space-y-3">
								<div
									v-for="n in 3"
									:key="n"
									class="border border-gray-100 px-4 py-3 animate-pulse bg-gray-50"
								>
									<div class="h-3 w-32 bg-gray-200 rounded mb-2" />
									<div class="h-3 w-24 bg-gray-100 rounded mb-1" />
									<div class="h-3 w-40 bg-gray-100 rounded" />
								</div>
							</div>
							<div
								v-else-if="allOrdersError"
								class="border border-red-200 text-red-700 px-4 py-3 text-sm bg-red-50"
							>
								{{ allOrdersError }}
							</div>
							<div
								v-else-if="!allOrders.length"
								class="border border-gray-200 px-4 py-3 text-sm text-gray-500 bg-gray-50"
							>
								Non sono ancora presenti ordini.
							</div>
							<div
								v-else
								class="space-y-3"
							>
								<div
									v-for="order in paginatedOrders"
									:key="order.id"
								>
									<div
										class="relative border border-gray-200 bg-white shadow-sm cursor-pointer hover:shadow-md transition-shadow"
										@click="openOrderDetail(order.id)"
									>
										<!-- Strisce colori servizi -->
										<div class="absolute inset-y-0 left-0 flex">
											<div
												v-for="(color, index) in getOrderServiceColors(order)"
												:key="index"
												class="w-0.5 sm:w-1"
												:style="{ backgroundColor: color }"
											/>
										</div>

										<!-- Contenuto card -->
										<div class="pl-4 sm:pl-5 pr-4 py-3 sm:py-3.5 flex flex-col gap-3 sm:flex-row sm:items-stretch sm:justify-between ml-[6px] sm:ml-[8px]">
											<!-- Colonna sinistra: tutti i dati ordine -->
											<div class="space-y-2 text-left flex-1">
												<p class="text-sm font-semibold text-gray-900">
													Ordine #{{ order.id }} ({{ formatOrderDate(order.purchase_date) }})
													<span
														v-if="order.invoice_code"
														class="text-xs font-normal text-gray-500 ml-1"
													>
														{{ order.invoice_code }}
													</span>
												</p>
												<div class="flex flex-col items-start gap-1.5 text-xs sm:text-sm">
													<span class="text-gray-700">
														Totale lordo:
														<span class="font-medium">{{ formatCurrency(order.total_gross_price) }}</span>
													</span>
													<div class="flex flex-wrap items-center gap-2">
														<span
															v-if="order.total_to_be_payed"
															:class="order.status === 'expired' ? 'text-[#A21B1F]' : 'text-gray-700'"
														>
															Da pagare:
															<span class="font-semibold">
																{{ formatCurrency(order.total_to_be_payed) }}
																<span
																	v-if="getOrderNextPaymentDueDate(order)"
																	class="font-normal text-xs text-[#A21B1F]"
																>
																	(scad. {{ formatOrderDate(getOrderNextPaymentDueDate(order)) }})
																</span>
															</span>
														</span>
														<span
															v-if="order.total_payed != null"
															class="text-gray-500"
														>
															Pagato:
															<span class="font-medium">{{ formatCurrency(order.total_payed) }}</span>
														</span>
													</div>
												</div>
											</div>

											<!-- Colonna destra: stato + servizi (ancorati in basso) -->
											<div class="flex flex-col items-end justify-between text-right sm:text-right min-h-full">
												<!-- Stato ordine in alto -->
												<p class="text-xs mb-1">
													<span
														class="inline-flex items-center px-2 py-0.5 rounded-full border"
														:class="getOrderStatusChipClass(order.status)"
													>
														{{ formatOrderStatus(order.status) }}
													</span>
												</p>

												<!-- Servizi acquistati + donut ancorati in basso -->
												<div
													v-if="getOrderPurchasedServices(order).length"
													class="flex flex-col items-end gap-1 mt-auto"
												>
													<p class="text-xs text-gray-600">
														Servizi acquistati: {{ getOrderPurchasedServices(order).length }}
													</p>
													<div class="flex flex-wrap justify-end gap-2">
														<div
															v-for="service in getOrderPurchasedServices(order)"
															:key="service.id"
															class="w-5 h-5 sm:w-6 sm:h-6 relative"
														>
															<svg
																viewBox="0 0 36 36"
																class="w-full h-full transform -rotate-90"
																aria-hidden="true"
															>
																<circle
																	class="text-gray-200"
																	stroke="currentColor"
																	stroke-width="8"
																	fill="transparent"
																	r="14"
																	cx="18"
																	cy="18"
																/>
																<circle
																	stroke-width="8"
																	fill="transparent"
																	r="14"
																	cx="18"
																	cy="18"
																	:stroke="service.service_color || '#A21B1F'"
																	:stroke-dasharray="`${Math.round(calculateServiceUsagePercentage(service) * 100)}, 100`"
																	stroke-linecap="round"
																/>
															</svg>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div
									v-if="totalOrdersPages > 1"
									class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-gray-100 mt-2 text-xs sm:text-sm text-gray-600"
								>
									<p>
										Pagina {{ currentOrdersPage }} di {{ totalOrdersPages }}
									</p>
									<div class="flex items-center gap-2">
										<button
											type="button"
											class="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 bg-white text-xs sm:text-sm uppercase font-button disabled:opacity-50 disabled:cursor-not-allowed"
											:disabled="currentOrdersPage === 1"
											@click="currentOrdersPage = Math.max(1, currentOrdersPage - 1)"
										>
											<span class="material-symbols-outlined text-sm sm:text-base">chevron_left</span>
											Precedente
										</button>
										<button
											type="button"
											class="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 bg-white text-xs sm:text-sm uppercase font-button disabled:opacity-50 disabled:cursor-not-allowed"
											:disabled="currentOrdersPage === totalOrdersPages"
											@click="currentOrdersPage = Math.min(totalOrdersPages, currentOrdersPage + 1)"
										>
											Successiva
											<span class="material-symbols-outlined text-sm sm:text-base">chevron_right</span>
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Blocco: QUESTIONARI -->
			<section class="mt-10 md:mt-12">
				<div class="flex items-center justify-between mb-6">
					<h2 class="text-lg font-bold text-gray-900">
						QUESTIONARI
					</h2>
					<BaseButton
						v-if="latestWizard && !latestWizard.has_completed_submission"
						:id="'questionario-latest'"
						:label="latestWizard.title || 'Compila il questionario'"
						variant="secondary"
						size="sm"
						icon="arrow"
						icon-position="right"
						type="url"
						url="/questionario"
					/>
				</div>

				<div class="-mx-4 sm:mx-0">
					<div class="px-4 sm:px-0">
						<div v-if="isWizardSubmissionsLoading" class="bg-white border border-gray-200 shadow-sm rounded-none px-5 py-5">
							<div class="space-y-3 animate-pulse">
								<div
									v-for="n in 3"
									:key="n"
									class="h-16 rounded border border-gray-100 bg-gray-50"
								/>
							</div>
						</div>
						<div
							v-else-if="wizardSubmissionsError"
							class="bg-white border border-red-200 text-red-700 shadow-sm rounded-none px-5 py-5 text-sm"
						>
							{{ wizardSubmissionsError }}
						</div>
						<div
							v-else-if="!wizardSubmissions.length"
							class="bg-white border border-gray-200 shadow-sm rounded-none px-5 py-5 text-sm text-gray-500"
						>
							Non hai ancora compilato nessun questionario.
						</div>
						<div
							v-else
							class="space-y-3"
						>
							<article
								v-for="submission in wizardSubmissions"
								:key="submission.id"
								class="border-l-4 border border-gray-200 bg-white shadow-sm overflow-hidden rounded-none transition-shadow hover:shadow-md cursor-pointer"
								:style="{ borderLeftColor: submission.score_result_color || '#A21B1F' }"
								@click="selectedSubmission = submission"
							>
								<div class="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
									<div class="min-w-0 space-y-0.5">
										<p class="text-sm font-semibold text-gray-900 truncate">
											{{ submission.wizard_title || 'Questionario' }}
										</p>
										<p class="text-xs text-gray-600">
											Compilato il
											<span class="font-medium">
												{{ formatWizardSubmissionDate(submission.date_created) }}
											</span>
										</p>
									</div>
									<div class="flex flex-col items-start sm:items-end gap-1 text-xs">
										<p class="text-gray-600">
											Risultato:
										</p>
										<div class="flex items-center gap-1">
											<span
												v-if="submission.score_result_icon"
												class="material-symbols-outlined text-base"
												:style="submission.score_result_color ? { color: submission.score_result_color } : undefined"
												aria-hidden="true"
											>
												{{ submission.score_result_icon }}
											</span>
											<span
												class="font-medium"
												:style="submission.score_result_color ? { color: submission.score_result_color } : undefined"
											>
												{{ submission.score_result_title || (submission.status === 'completed' ? 'Completato' : 'In corso') }}
											</span>
										</div>
									</div>
								</div>
							</article>
						</div>
					</div>
				</div>
			</section>

			<!-- Blocco: PRIVACY E SICUREZZA -->
			<section class="mt-10 md:mt-12">
				<h2 class="text-lg font-bold text-gray-900 mb-6">
					PRIVACY E SICUREZZA
				</h2>

				<div class="-mx-4 sm:mx-0">
					<div class="px-4 sm:px-0">
						<div class="bg-white border border-gray-200 shadow-sm rounded-none px-5 py-6 space-y-4">
							<p class="text-sm text-gray-700">
								Abbiamo a cuore la tua privacy e la trasparenza su come utilizziamo i tuoi dati.
								<br>
								Qui puoi consultare i Termini e Condizioni, la nostra Privacy Policy o gestire il tuo account.
							</p>
								<div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
								<div class="flex flex-wrap gap-3">
									<button
										type="button"
										class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-sm sm:text-base font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 rounded-none transition-colors uppercase"
										@click="router.push('/termini-e-condizioni')"
									>
										TERMINI E CONDIZIONI
									</button>
									<button
										type="button"
										class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-sm sm:text-base font-medium font-button text-black bg-white border-2 border-black hover:bg-gray-50 rounded-none transition-colors uppercase"
										@click="router.push('/privacy-policy')"
									>
										PRIVACY POLICY
									</button>
								</div>

								<div class="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto">
									<button
										type="button"
										class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-sm sm:text-base font-medium font-button text-white bg-[#A21B1F] hover:bg-[#A21B1F]/90 rounded-none transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
										:disabled="isDeletingAccount"
										@click="showDeleteAccountDialog = true"
									>
										ELIMINA ACCOUNT
									</button>
									<p
										v-if="deleteAccountError"
										class="text-xs text-red-600"
									>
										{{ deleteAccountError }}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			<template #fallback>
				<div
					class="border-l-4 bg-white shadow-sm overflow-hidden transition-shadow flex items-center justify-center min-h-[200px] text-gray-400"
					style="border-left-color: #A21B1F;"
				>
					<span class="text-sm">Caricamento profilo…</span>
				</div>
			</template>
		</ClientOnly>
	</div>

	<!-- Pannello modifica profilo -->
	<AreaRiservataProfiloEditPanel
		v-if="showEditPanel"
		:open="showEditPanel"
		@close="closeEditProfile"
		@saved="onProfileSaved"
	/>

	<!-- Pannello modifica singolo indirizzo -->
	<AreaRiservataIndirizzoEditPanel
		v-if="showAddressPanel && editingAddress"
		:open="showAddressPanel"
		:customer-address="editingAddress"
		@close="closeEditAddress"
		@saved="onAddressSaved"
	/>

	<!-- Pannello aggiungi indirizzo -->
	<AreaRiservataIndirizzoAddPanel
		v-if="showAddAddressPanel"
		:open="showAddAddressPanel"
		@close="closeAddAddress"
		@saved="onAddressAdded"
	/>

	<!-- Dialog conferma eliminazione questionario -->
	<BaseConfirmDialog
		v-model="showDeleteSubmissionDialog"
		v-if="selectedSubmission"
		title="Elimina questionario"
		description="Sei sicuro di voler eliminare questo questionario? L'operazione non può essere annullata."
		confirm-label="ELIMINA QUESTIONARIO"
		@confirm="confirmDeleteSelectedSubmission"
	/>

	<!-- Dialog conferma eliminazione account -->
	<BaseConfirmDialog
		v-model="showDeleteAccountDialog"
		title="Elimina account"
		description="Sei sicuro di voler eliminare il tuo account? Questa operazione è definitiva e non potrà essere annullata."
		confirm-label="ELIMINA ACCOUNT"
		@confirm="confirmDeleteAccount"
	/>

	<!-- Pannello dettaglio membro staff (drawer a destra, sola lettura) -->
	<Teleport to="body">
		<div
			v-if="selectedStaff"
			class="fixed inset-0 z-50 flex"
			aria-modal="true"
			role="dialog"
			aria-label="Dettaglio membro dello staff"
		>
			<button
				type="button"
				class="absolute inset-0 bg-black/50 transition-opacity"
				aria-label="Chiudi dettagli staff"
				@click="selectedStaff = null"
			/>
			<div
				class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
				@click.stop
			>
				<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900">
						Dettaglio Staff
					</h2>
					<button
						type="button"
						class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
						aria-label="Chiudi dettagli staff"
						@click="selectedStaff = null"
					>
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>

				<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4">
					<!-- Card principale staff, coerente con lo slider "IL TUO STAFF WEFOCUS" -->
					<div class="bg-white border border-gray-200 shadow-sm rounded-none px-5 py-4">
						<div class="flex flex-col items-center text-center gap-3">
							<div class="w-16 h-16 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center">
								<img
									v-if="staffAvatarUrl(selectedStaff)"
									:src="staffAvatarUrl(selectedStaff)!"
									:alt="staffFullName(selectedStaff)"
									class="w-full h-full object-cover"
								>
								<span
									v-else
									class="material-symbols-outlined text-4xl text-gray-400"
									aria-hidden="true"
								>
									person
								</span>
							</div>
							<div class="space-y-1">
								<p class="text-base font-semibold text-gray-900">
									{{ staffFullName(selectedStaff) }}
								</p>
							<p class="text-sm text-gray-600">
									{{ selectedStaff.job_position || 'Professionista WeFocus' }}
								</p>
							<p
								v-if="selectedStaff.description"
								class="text-sm text-gray-800 whitespace-pre-line mt-1 text-left"
							>
								{{ selectedStaff.description }}
							</p>
							</div>
						</div>
					</div>

					<div v-if="selectedStaff.resume_files && selectedStaff.resume_files.length" class="text-sm space-y-2">
						<h4 class="font-medium text-gray-900">SCOPRI DI PIÙ</h4>
						<ul class="space-y-3">
							<li
								v-for="file in selectedStaff.resume_files"
								:key="file.id"
							>
								<a
									:href="staffResumeUrl(file) || undefined"
									target="_blank"
									rel="noopener noreferrer"
									class="relative block border border-gray-200 bg-white shadow-sm hover:shadow-md rounded-none px-4 py-3 transition-shadow"
								>
									<div class="pr-7">
										<p class="text-sm font-medium text-gray-900 truncate">
											{{ file.filename_download || 'Curriculum' }}
										</p>
										<p class="mt-1 text-xs text-gray-600">
											Clicca per scaricare
										</p>
									</div>
									<span
										class="material-symbols-outlined text-lg text-gray-700 absolute bottom-2 right-3"
										aria-hidden="true"
									>
										download
									</span>
								</a>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</Teleport>

	<!-- Pannello dettaglio ordine (drawer a destra, sola lettura) -->
	<Teleport to="body">
		<div
			v-if="selectedOrder"
			class="fixed inset-0 z-50 flex"
			aria-modal="true"
			role="dialog"
			aria-label="Dettaglio ordine"
		>
			<button
				type="button"
				class="absolute inset-0 bg-black/50 transition-opacity"
				aria-label="Chiudi dettaglio ordine"
				@click="selectedOrder = null"
			/>
			<div
				class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
				@click.stop
			>
				<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900">
						Dettaglio ordine
					</h2>
					<button
						type="button"
						class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
						aria-label="Chiudi dettaglio ordine"
						@click="selectedOrder = null"
					>
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>

				<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm">
					<div v-if="isOrderDetailLoading">
						<div class="space-y-3 animate-pulse">
							<div class="h-4 w-40 bg-gray-200 rounded" />
							<div class="h-3 w-32 bg-gray-100 rounded" />
							<div class="h-3 w-24 bg-gray-100 rounded" />
						</div>
					</div>
					<div
						v-else-if="orderDetailError"
						class="text-sm text-red-600"
					>
						{{ orderDetailError }}
					</div>
					<div v-else>
						<!-- Card riassuntiva ordine (stessa struttura elenco) -->
						<div class="border border-gray-200 bg-white shadow-sm px-4 py-3 sm:px-5 sm:py-4 mb-4">
							<div class="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:justify-between">
								<!-- Colonna sinistra: dati ordine -->
								<div class="space-y-2 text-left flex-1">
									<p class="text-sm font-semibold text-gray-900">
										Ordine #{{ selectedOrder.id }} ({{ formatOrderDate(selectedOrder.purchase_date) }})
										<span
											v-if="selectedOrder.invoice_code"
											class="text-xs font-normal text-gray-500 ml-1"
										>
											{{ selectedOrder.invoice_code }}
										</span>
									</p>
									<div class="flex flex-col items-start gap-1.5 text-xs sm:text-sm">
										<span class="text-gray-700">
											Totale lordo:
											<span class="font-medium">
												{{ formatCurrency(selectedOrder.total_gross_price) }}
											</span>
										</span>
										<div class="flex flex-wrap items-center gap-2">
											<span
												v-if="selectedOrder.total_to_be_payed"
												:class="selectedOrder.status === 'expired' ? 'text-[#A21B1F]' : 'text-gray-700'"
											>
												Da pagare:
												<span class="font-semibold">
													{{ formatCurrency(selectedOrder.total_to_be_payed) }}
													<span
														v-if="getOrderNextPaymentDueDate(selectedOrder)"
														class="font-normal text-xs text-[#A21B1F]"
													>
														(scad. {{ formatOrderDate(getOrderNextPaymentDueDate(selectedOrder)) }})
													</span>
												</span>
											</span>
											<span
												v-if="selectedOrder.total_payed != null"
												class="text-gray-500"
											>
												Pagato:
												<span class="font-medium">
													{{ formatCurrency(selectedOrder.total_payed) }}
												</span>
											</span>
										</div>
									</div>
								</div>

								<!-- Colonna destra: stato + servizi -->
								<div class="flex flex-col items-end justify-between text-right sm:text-right min-h-full">
									<p class="text-xs mb-1">
										<span
											class="inline-flex items-center px-2 py-0.5 rounded-full border"
											:class="getOrderStatusChipClass(selectedOrder.status)"
										>
											{{ formatOrderStatus(selectedOrder.status) }}
										</span>
									</p>
									<div
										v-if="selectedOrder.customer_purchased_services && selectedOrder.customer_purchased_services.length"
										class="flex flex-col items-end gap-1 mt-auto"
									>
										<p class="text-xs text-gray-600">
											Servizi acquistati: {{ selectedOrder.customer_purchased_services.length }}
										</p>
										<div class="flex flex-wrap justify-end gap-2">
											<div
												v-for="item in selectedOrder.customer_purchased_services"
												:key="item.id"
												class="w-5 h-5 sm:w-6 sm:h-6 relative"
											>
												<svg
													viewBox="0 0 36 36"
													class="w-full h-full transform -rotate-90"
													aria-hidden="true"
												>
													<circle
														class="text-gray-200"
														stroke="currentColor"
														stroke-width="8"
														fill="transparent"
														r="14"
														cx="18"
														cy="18"
													/>
													<circle
														stroke-width="8"
														fill="transparent"
														r="14"
														cx="18"
														cy="18"
														:stroke="item.service?.color || '#A21B1F'"
														:stroke-dasharray="`${Math.round(calculateServiceUsagePercentage({
															purchased_quantity: item.purchased_quantity || 0,
															remaining_quantity: item.computed_remaining_quantity ?? null,
														}) * 100)}, 100`"
														stroke-linecap="round"
													/>
												</svg>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div v-if="selectedOrder.customer" class="mt-3">
							<div class="border border-gray-200 bg-white px-3 py-3 text-xs sm:text-sm space-y-1.5">
								<p class="text-sm font-medium text-gray-900">
									{{ selectedOrder.customer.first_name }} {{ selectedOrder.customer.last_name }}
								</p>
								<p class="text-xs text-gray-600">
									<span v-if="selectedOrder.customer.email">{{ selectedOrder.customer.email }}</span>
									<span v-if="selectedOrder.customer.phone"> • {{ selectedOrder.customer.phone }}</span>
								</p>
								<p
									v-if="selectedOrder.customer.tax_code || selectedOrder.customer.vat_number"
									class="text-xs text-gray-600"
								>
									<span v-if="selectedOrder.customer.tax_code">CF: {{ selectedOrder.customer.tax_code }}</span>
									<span v-if="selectedOrder.customer.vat_number">
										<span v-if="selectedOrder.customer.tax_code"> • </span>
										P.IVA: {{ selectedOrder.customer.vat_number }}
									</span>
								</p>
								<p
									v-if="selectedOrder.customer.billing_code"
									class="text-xs text-gray-600"
								>
									Codice SDI: {{ selectedOrder.customer.billing_code }}
								</p>
							</div>
						</div>

						<div
							v-if="selectedOrder.customer_purchased_services && selectedOrder.customer_purchased_services.length"
							class="border-t border-gray-100 pt-3 mt-3"
						>
							<h3 class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
								Dettaglio servizi acquistati
							</h3>
							<ul class="space-y-2">
								<li
									v-for="item in selectedOrder.customer_purchased_services"
									:key="item.id"
									class="border border-gray-200 bg-white px-3 py-2 flex items-start justify-between gap-3"
								>
									<div class="min-w-0 space-y-1">
										<p class="text-sm font-medium text-gray-900">
											{{ item.service?.name || 'Servizio' }}
										</p>
										<p class="text-xs text-gray-700">
											<span v-if="item.gross_price != null">
												{{ formatCurrency(item.gross_price) }}
											</span>
											<span
												v-else-if="item.unit_gross_price != null && item.purchased_quantity"
											>
												{{ formatCurrency(item.unit_gross_price) }} x {{ item.purchased_quantity }}
											</span>
										</p>
										<p
											v-if="item.valid_to"
											class="text-[11px] text-gray-500"
										>
											Scadenza: {{ formatOrderDate(item.valid_to) }}
										</p>
									</div>
									<div class="flex flex-col items-end gap-1 text-xs text-gray-700">
										<div class="w-6 h-6 sm:w-7 sm:h-7 relative">
											<svg
												viewBox="0 0 36 36"
												class="w-full h-full transform -rotate-90"
												aria-hidden="true"
											>
												<circle
													class="text-gray-200"
													stroke="currentColor"
													stroke-width="8"
													fill="transparent"
													r="14"
													cx="18"
													cy="18"
												/>
												<circle
													stroke-width="8"
													fill="transparent"
													r="14"
													cx="18"
													cy="18"
													:stroke="item.service?.color || '#A21B1F'"
													:stroke-dasharray="`${Math.round(calculateServiceUsagePercentage({
														purchased_quantity: item.purchased_quantity || 0,
														remaining_quantity: item.computed_remaining_quantity ?? null,
													}) * 100)}, 100`"
													stroke-linecap="round"
												/>
											</svg>
										</div>
										<p class="text-[11px] text-gray-600">
											{{ item.used_quantity || 0 }} / {{ item.purchased_quantity || 0 }} usati
										</p>
									</div>
								</li>
							</ul>
						</div>

						<div
							v-if="selectedOrder.payments && selectedOrder.payments.length"
							class="border-t border-gray-100 pt-3 mt-3"
						>
							<h3 class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
								Pagamenti / Rate
							</h3>
							<ul class="space-y-2">
								<li
									v-for="payment in selectedOrder.payments"
									:key="payment.id"
									class="border border-gray-200 bg-white px-3 py-2 flex items-center justify-between gap-3 text-xs"
								>
									<div class="space-y-0.5">
										<p class="text-gray-900">
											{{ formatCurrency(payment.amount) }}
										</p>
										<p class="text-[11px] text-gray-600">
											Scadenza: {{ formatOrderDate(payment.due_date) }}
											<span v-if="payment.payment_date">
												• Pagato: {{ formatOrderDate(payment.payment_date) }}
											</span>
										</p>
									</div>
									<span
										class="inline-flex items-center px-2 py-0.5 rounded-full border text-[11px]"
										:class="payment.status === 'payed'
											? 'border-emerald-200 text-emerald-700 bg-emerald-50'
											: payment.status === 'expired'
												? 'border-red-200 text-red-700 bg-red-50'
												: 'border-gray-200 text-gray-700 bg-gray-50'"
									>
										{{ payment.status === 'payed'
											? 'Pagato'
											: payment.status === 'expired'
												? 'Scaduto'
												: 'Da pagare' }}
									</span>
								</li>
							</ul>
						</div>

					</div>
				</div>
			</div>
		</div>
	</Teleport>

	<!-- Pannello dettaglio questionario (drawer a destra, sola lettura) -->
	<Teleport to="body">
		<div
			v-if="selectedSubmission"
			class="fixed inset-0 z-50 flex"
			aria-modal="true"
			role="dialog"
			aria-label="Dettaglio questionario"
		>
			<button
				type="button"
				class="absolute inset-0 bg-black/50 transition-opacity"
				aria-label="Chiudi dettaglio questionario"
				@click="selectedSubmission = null"
			/>
			<div
				class="relative ml-auto w-full max-w-md sm:max-w-lg bg-white shadow-xl flex flex-col max-h-full overflow-hidden"
				@click.stop
			>
				<div class="flex items-center justify-between shrink-0 px-4 py-3 border-b border-gray-200">
					<h2 class="text-lg font-semibold text-gray-900">
						Dettaglio questionario
					</h2>
					<button
						type="button"
						class="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors"
						aria-label="Chiudi dettaglio questionario"
						@click="selectedSubmission = null"
					>
						<span class="material-symbols-outlined">close</span>
					</button>
				</div>

				<div class="flex-1 overflow-y-auto px-4 py-4 space-y-4 text-sm">
					<div
						class="border-l-4 bg-white shadow-sm overflow-hidden"
						:style="{ borderLeftColor: selectedSubmission.score_result_color || '#A21B1F' }"
					>
						<div class="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
							<div class="min-w-0 space-y-1">
								<p class="text-sm font-semibold text-gray-900 truncate">
									{{ selectedSubmission.wizard_title || 'Questionario' }}
								</p>
								<p class="text-xs text-gray-600">
									Compilato il
									<span class="font-medium">
										{{ formatWizardSubmissionDate(selectedSubmission.date_created) }}
									</span>
								</p>
							</div>
							<div class="flex flex-col items-start sm:items-end gap-1 text-xs">
								<p class="text-gray-600">
									Risultato:
								</p>
								<div class="flex items-center gap-1">
									<span
										v-if="selectedSubmission.score_result_icon"
										class="material-symbols-outlined text-base"
										:style="selectedSubmission.score_result_color ? { color: selectedSubmission.score_result_color } : undefined"
										aria-hidden="true"
									>
										{{ selectedSubmission.score_result_icon }}
									</span>
									<span
										class="font-medium"
										:style="selectedSubmission.score_result_color ? { color: selectedSubmission.score_result_color } : undefined"
									>
										{{ selectedSubmission.score_result_title || (selectedSubmission.status === 'completed' ? 'Completato' : 'In corso') }}
									</span>
								</div>
							</div>
						</div>
					</div>

					<div
						v-if="selectedSubmission.score_result_description"
						class="border-t border-gray-100 pt-3 mt-2"
					>
						<h3 class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
							Valutazione
						</h3>
						<p class="text-sm text-gray-800 whitespace-pre-line">
							{{ selectedSubmission.score_result_description }}
						</p>
					</div>

					<div class="border-t border-gray-100 pt-3 mt-2">
						<h3 class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1.5">
							Domande e risposte
						</h3>
						<div v-if="!selectedSubmission.answers.length" class="text-xs text-gray-500">
							Nessuna risposta disponibile.
						</div>
						<ul
							v-else
							class="space-y-3"
						>
							<li
								v-for="answer in selectedSubmission.answers"
								:key="answer.id"
								class="border border-gray-100 rounded-none px-3 py-2"
							>
								<p class="text-sm font-medium text-gray-900">
									{{ answer.question || 'Domanda' }}
								</p>
								<p class="text-sm text-gray-700 mt-0.5">
									{{ formatWizardAnswer(answer) }}
								</p>
							</li>
						</ul>
					</div>

					<div class="border-t border-gray-100 pt-3 mt-4 flex justify-end">
						<button
							type="button"
							class="inline-flex items-center justify-center gap-2 h-10 px-5 py-4 text-lg font-medium font-button text-white bg-[#A21B1F] hover:bg-[#A21B1F]/90 rounded-none transition-colors uppercase disabled:opacity-50 disabled:cursor-not-allowed"
							:disabled="isDeletingSubmission"
							@click="showDeleteSubmissionDialog = true"
						>
							ELIMINA QUESTIONARIO
						</button>
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style scoped>
.no-scrollbar {
	-ms-overflow-style: none; /* IE e Edge */
	scrollbar-width: none; /* Firefox */
}

.no-scrollbar::-webkit-scrollbar {
	display: none; /* Chrome, Safari, Opera */
}

</style>
